import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Search,
  Bot,
  MessageCircle,
  UserPlus,
  Sparkles,
  Layers,
  Wallet,
  TrendingUp,
  Handshake,
  Shield,
  Droplets,
  Zap,
  ArrowRightLeft,
  Rocket,
  CreditCard,
  Banknote,
  Cpu,
  LineChart,
  Server,
  Megaphone,
} from 'lucide-react';
import { aiMatchmakingFeatures, topicCategories } from '../data/mock';

/* Realistic AI matchmaking prompts inspired by real DeFi builder needs */
const MATCH_QUERIES = [
  { prompt: 'You’re looking for a bridge for your dApp', matches: 16, Icon: ArrowRightLeft, color: '#38bdf8' },
  { prompt: 'You need more trading volume on your platform', matches: 8, Icon: LineChart, color: '#fbbf24' },
  { prompt: 'You’re open to co-marketing / joint AMAs', matches: 28, Icon: Megaphone, color: '#f472b6' },
  { prompt: 'You want grants to migrate to a new EVM chain', matches: 14, Icon: Wallet, color: '#a78bfa' },
  { prompt: 'You’re raising funds for your protocol', matches: 22, Icon: Rocket, color: '#4ade80' },
  { prompt: 'You’re looking to get a security audit done', matches: 7, Icon: Shield, color: '#60a5fa' },
  { prompt: 'You run a trading platform open to integrations', matches: 48, Icon: TrendingUp, color: '#22d3ee' },
  { prompt: 'You offer crypto payroll services', matches: 32, Icon: Banknote, color: '#84cc16' },
  { prompt: 'You run a Neo Bank with crypto corporate cards', matches: 53, Icon: CreditCard, color: '#f97316' },
  { prompt: 'You run a user-acquisition platform for DeFi', matches: 64, Icon: Sparkles, color: '#e879f9' },
  { prompt: 'Your ecosystem wants DeFAI-Agent builders', matches: 42, Icon: Cpu, color: '#c084fc' },
  { prompt: 'You offer price feeds as a service', matches: 31, Icon: Droplets, color: '#2dd4bf' },
  { prompt: 'You offer infra for DeFi protocols', matches: 87, Icon: Server, color: '#facc15' },
];

const NODES = [Layers, Wallet, TrendingUp, Handshake, Shield, Droplets, Rocket, Cpu];

/* Scrolling category tags */
const ScrollingCategories = () => {
  const categories = Object.entries(topicCategories);
  const doubled = [...categories, ...categories];

  return (
    <div className="overflow-hidden py-2">
      <motion.div
        className="flex gap-2 whitespace-nowrap"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ x: { repeat: Infinity, repeatType: 'loop', duration: 20, ease: 'linear' } }}
      >
        {doubled.map(([key, value], index) => (
          <span
            key={`${key}-${index}`}
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-muted)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] transition-colors cursor-pointer"
          >
            {value.label}
          </span>
        ))}
      </motion.div>
    </div>
  );
};

/* Radar canvas · uses absolute pixel size so YOU + nodes + beam align pixel-perfectly */
const RADAR_SIZE = 320; // px
const NODE_RADIUS = 128; // distance from center to nodes
const SM_SIZE = 260;
const SM_RADIUS = 105;

const MatchRadar = () => {
  const [activeQuery, setActiveQuery] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [size, setSize] = useState(RADAR_SIZE);
  const [radius, setRadius] = useState(NODE_RADIUS);

  // Responsive radar sizing — bind once on mount, cleanup on unmount
  useEffect(() => {
    const update = () => {
      const isSmall = window.innerWidth < 640;
      setSize(isSmall ? SM_SIZE : RADAR_SIZE);
      setRadius(isSmall ? SM_RADIUS : NODE_RADIUS);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slow continuous orbit rotation — start timer once
  useEffect(() => {
    const id = setInterval(() => setRotation((r) => (r + 0.35) % 360), 60);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-cycle matchmaking queries — start timer once
  useEffect(() => {
    const id = setInterval(() => setActiveQuery((p) => (p + 1) % MATCH_QUERIES.length), 3200);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const query = MATCH_QUERIES[activeQuery];
  const step = 360 / NODES.length;
  const center = size / 2;
  // Active node is always the one closest to the sweep leading edge (top-right area at 315deg)
  const activeIdx = activeQuery % NODES.length;
  const activeAngle = (activeIdx * step + rotation) % 360;
  const ax = center + Math.cos((activeAngle * Math.PI) / 180) * radius;
  const ay = center + Math.sin((activeAngle * Math.PI) / 180) * radius;

  return (
    <div className="relative rounded-3xl bg-[rgba(12,14,18,0.7)] border border-white/8 p-4 sm:p-5 overflow-hidden backdrop-blur-xl">
      {/* backdrop noise + glow */}
      <div
        className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{ background: 'var(--accent-primary)' }}
      />
      <div
        className="absolute -bottom-32 -left-24 w-72 h-72 rounded-full blur-3xl opacity-15 pointer-events-none"
        style={{ background: query.color }}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-3 relative z-10 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <motion.div
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 1.8, repeat: Infinity }}
            className="w-8 h-8 rounded-lg bg-[var(--accent-bg)] border border-[var(--accent-primary)] flex items-center justify-center flex-shrink-0"
          >
            <Bot className="w-4 h-4 text-[var(--accent-primary)]" />
          </motion.div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-white leading-tight">AI Matchmaker</h4>
            <p className="text-[10px] text-[var(--text-muted)] flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)] animate-pulse" />
              Scanning 2,400+ protocols
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[9px] uppercase tracking-widest text-[var(--text-muted)]">Matches</div>
          <motion.div
            key={activeQuery}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-lg font-bold leading-none"
            style={{ color: query.color }}
          >
            {query.matches}
          </motion.div>
        </div>
      </div>

      {/* Radar canvas — fixed square so YOU is truly centered */}
      <div className="relative mx-auto" style={{ width: size, height: size }}>
        {/* Concentric rings */}
        {[0.35, 0.6, 0.85].map((r, i) => (
          <div
            key={i}
            className="absolute rounded-full border border-white/6"
            style={{
              width: size * r,
              height: size * r,
              left: (size - size * r) / 2,
              top: (size - size * r) / 2,
            }}
          />
        ))}

        {/* Cross-hair guides */}
        <div className="absolute left-1/2 top-2 bottom-2 w-px bg-white/4 -translate-x-1/2" />
        <div className="absolute top-1/2 left-2 right-2 h-px bg-white/4 -translate-y-1/2" />

        {/* Sweeping radar arc — centered on the canvas */}
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{
            width: size * 0.9,
            height: size * 0.9,
            left: (size - size * 0.9) / 2,
            top: (size - size * 0.9) / 2,
            background:
              'conic-gradient(from 0deg, transparent 0deg, transparent 300deg, rgba(218,255,1,0.28) 342deg, rgba(218,255,1,0.6) 358deg, transparent 360deg)',
            maskImage: 'radial-gradient(circle, black 55%, transparent 100%)',
            WebkitMaskImage: 'radial-gradient(circle, black 55%, transparent 100%)',
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 4.5, repeat: Infinity, ease: 'linear' }}
        />

        {/* Beam from center to active node — SVG covers full canvas */}
        <svg
          className="absolute inset-0 pointer-events-none"
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
        >
          <defs>
            <linearGradient id="beam-grad" x1={center} y1={center} x2={ax} y2={ay} gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor={query.color} stopOpacity="0.95" />
              <stop offset="100%" stopColor={query.color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.line
            key={activeQuery}
            x1={center}
            y1={center}
            x2={ax}
            y2={ay}
            stroke="url(#beam-grad)"
            strokeWidth="1.75"
            strokeDasharray="3 4"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <animate attributeName="stroke-dashoffset" from="0" to="-14" dur="0.9s" repeatCount="indefinite" />
          </motion.line>
          {[0.4, 0.65, 0.88].map((t, i) => (
            <motion.circle
              key={`${activeQuery}-${i}`}
              cx={center + (ax - center) * t}
              cy={center + (ay - center) * t}
              r="2.5"
              fill={query.color}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.35, ease: 'easeInOut' }}
            />
          ))}
        </svg>

        {/* Orbit nodes */}
        {NODES.map((Icon, i) => {
          const angle = (i * step + rotation) % 360;
          const nx = center + Math.cos((angle * Math.PI) / 180) * radius;
          const ny = center + Math.sin((angle * Math.PI) / 180) * radius;
          const isActive = i === activeIdx;
          return (
            <motion.div
              key={i}
              className="absolute"
              style={{ left: nx, top: ny, transform: 'translate(-50%, -50%)' }}
              animate={{
                scale: isActive ? 1.18 : 1,
                boxShadow: isActive
                  ? `0 0 0 3px ${query.color}33, 0 12px 28px -6px ${query.color}88`
                  : '0 6px 18px rgba(0,0,0,0.4)',
              }}
              transition={{ duration: 0.4 }}
            >
              <div
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl border flex items-center justify-center"
                style={{
                  background: isActive
                    ? `linear-gradient(135deg, ${query.color}22, ${query.color}05)`
                    : 'rgba(255,255,255,0.03)',
                  borderColor: isActive ? `${query.color}88` : 'rgba(255,255,255,0.08)',
                }}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: isActive ? query.color : '#8a8f99' }} />
              </div>
            </motion.div>
          );
        })}

        {/* Center YOU hub — perfectly centered */}
        <div
          className="absolute z-20"
          style={{ left: center, top: center, transform: 'translate(-50%, -50%)' }}
        >
          <motion.div
            animate={{
              boxShadow: [
                '0 0 0 0 rgba(218,255,1,0.55)',
                '0 0 0 18px rgba(218,255,1,0)',
              ],
            }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, var(--accent-primary), rgba(218,255,1,0.6))',
              boxShadow: '0 12px 30px -6px rgba(218,255,1,0.4)',
            }}
          >
            <span className="text-[10px] font-black uppercase tracking-wider text-[var(--bg-primary)]">YOU</span>
          </motion.div>
        </div>
      </div>

      {/* Active query card */}
      <div className="relative z-10 mt-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeQuery}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="p-3 sm:p-4 rounded-2xl border flex items-center gap-3"
            style={{
              background: `linear-gradient(135deg, ${query.color}18, rgba(255,255,255,0.02))`,
              borderColor: `${query.color}55`,
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: `${query.color}25`,
                border: `1px solid ${query.color}66`,
              }}
            >
              <query.Icon className="w-5 h-5" style={{ color: query.color }} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-0.5">Prompt · You said</div>
              <div className="text-xs sm:text-sm font-semibold text-white leading-snug truncate">
                {query.prompt}
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <motion.div
                key={query.matches}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-lg sm:text-xl font-black leading-none"
                style={{ color: query.color }}
              >
                {query.matches}
              </motion.div>
              <div className="text-[9px] text-[var(--text-muted)] uppercase tracking-wider">matches</div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

const AIMatchmakingSection = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const icons = [Search, Sparkles, UserPlus, MessageCircle];

  return (
    <section ref={ref} className="section-padding" data-testid="ai-matchmaking-section">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs sm:text-sm text-[var(--accent-primary)] uppercase tracking-widest font-medium">
              AI-Powered Matchmaking
            </span>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mt-3 sm:mt-4 mb-3 sm:mb-4 md:mb-6">
              Connections That
              <span className="text-[var(--accent-primary)]"> Actually Matter</span>
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-[var(--text-secondary)] mb-4">
              Tell the algorithm what you’re working on and it surfaces the exact protocols, ecosystems and builders you should meet. No random networking, just meaningful conversations that drive results.
            </p>

            <div className="mb-4 sm:mb-6 md:mb-8">
              <p className="text-[9px] sm:text-[10px] text-[var(--text-muted)] mb-2 uppercase tracking-wider">
                Match across 35+ DeFi categories
              </p>
              <ScrollingCategories />
            </div>

            <div className="space-y-3 sm:space-y-4 md:space-y-5">
              {aiMatchmakingFeatures.map((feature, index) => {
                const Icon = icons[index];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    className="flex gap-3 sm:gap-4"
                  >
                    <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-xl bg-[var(--accent-bg)] border border-[var(--accent-primary)]/50 flex items-center justify-center">
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--accent-primary)]" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm sm:text-base mb-0.5 sm:mb-1">{feature.title}</h3>
                      <p className="text-xs sm:text-sm text-[var(--text-muted)]">{feature.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right - Match Radar with realistic prompts */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <MatchRadar />

            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mt-4 p-3 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-subtle)]">
              <div className="text-center">
                <div className="text-sm sm:text-base font-bold text-[var(--accent-primary)]">3.2x</div>
                <div className="text-[8px] text-[var(--text-muted)]">Engagement</div>
              </div>
              <div className="w-px h-6 bg-[var(--border-subtle)]" />
              <div className="text-center">
                <div className="text-sm sm:text-base font-bold text-[var(--accent-primary)]">1-Click</div>
                <div className="text-[8px] text-[var(--text-muted)]">Join Tables</div>
              </div>
              <div className="w-px h-6 bg-[var(--border-subtle)]" />
              <div className="text-center">
                <div className="text-sm sm:text-base font-bold text-[var(--accent-primary)]">35+</div>
                <div className="text-[8px] text-[var(--text-muted)]">Categories</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AIMatchmakingSection;
